/* =========================================================
   DRLs Direct – Shared article interactions
   - Accessible progressive tabs
   - Data-driven vehicle selector
   ========================================================= */

(() => {
  "use strict";

  const SELECTORS = {
    article: ".dd-article",
    tabs: "[data-dd-tabs]",
    tabList: "[data-dd-tab-list]",
    tab: "[data-dd-tab]",
    tabPanel: "[data-dd-tab-panel]",
    vehicleSelector: "[data-dd-vehicle-selector]",
    brandSelect: "[data-dd-brand]",
    modelSelect: "[data-dd-model]",
    vehicleAction: "[data-dd-vehicle-action]",
    vehicleData: "[data-dd-vehicle-data]",
    vehicleStatus: "[data-dd-vehicle-status]"
  };

  const getArticleRoots = (scope) => {
    const roots = [];

    if (scope instanceof Element && scope.matches(SELECTORS.article)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll(SELECTORS.article));
    }

    return roots;
  };

  const makeSafeId = (value) =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const getSafeProductUrl = (value) => {
    const candidate = String(value || "").trim();

    if (!candidate) {
      return "";
    }

    try {
      const parsedUrl = new URL(candidate, window.location.origin);
      return ["http:", "https:"].includes(parsedUrl.protocol) ? candidate : "";
    } catch (error) {
      return "";
    }
  };

  const initializeTabs = (root) => {
    root.querySelectorAll(SELECTORS.tabs).forEach((tabs, groupIndex) => {
      if (tabs.dataset.ddReady === "true") {
        return;
      }

      const tabList = tabs.querySelector(SELECTORS.tabList);
      const tabButtons = Array.from(tabs.querySelectorAll(SELECTORS.tab));
      const panels = Array.from(tabs.querySelectorAll(SELECTORS.tabPanel));

      if (!tabList || tabButtons.length === 0 || panels.length === 0) {
        return;
      }

      const groupId = tabs.id || `dd-tabs-${groupIndex + 1}`;
      tabs.id = groupId;
      tabList.setAttribute("role", "tablist");

      const getPanelForTab = (tab) => {
        const key = tab.dataset.ddTab;
        return panels.find((panel) => panel.dataset.ddTabPanel === key) || null;
      };

      const activateTab = (selectedTab, moveFocus = false) => {
        const selectedPanel = getPanelForTab(selectedTab);

        if (!selectedPanel) {
          return;
        }

        tabButtons.forEach((tab) => {
          const isSelected = tab === selectedTab;
          tab.setAttribute("aria-selected", String(isSelected));
          tab.setAttribute("tabindex", isSelected ? "0" : "-1");
        });

        panels.forEach((panel) => {
          panel.hidden = panel !== selectedPanel;
        });

        if (moveFocus) {
          selectedTab.focus();
        }
      };

      tabButtons.forEach((tab, index) => {
        const key = tab.dataset.ddTab;
        const panel = getPanelForTab(tab);

        if (!key || !panel) {
          return;
        }

        const safeKey = makeSafeId(key) || String(index + 1);
        const tabId = tab.id || `${groupId}-tab-${safeKey}`;
        const panelId = panel.id || `${groupId}-panel-${safeKey}`;

        tab.id = tabId;
        tab.type = "button";
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-controls", panelId);

        panel.id = panelId;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tabId);
        panel.setAttribute("tabindex", "0");

        tab.addEventListener("click", () => activateTab(tab));

        tab.addEventListener("keydown", (event) => {
          const currentIndex = tabButtons.indexOf(tab);
          let nextIndex = currentIndex;

          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            nextIndex = (currentIndex + 1) % tabButtons.length;
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
          } else if (event.key === "Home") {
            nextIndex = 0;
          } else if (event.key === "End") {
            nextIndex = tabButtons.length - 1;
          } else {
            return;
          }

          event.preventDefault();
          activateTab(tabButtons[nextIndex], true);
        });
      });

      const initiallySelected =
        tabButtons.find((tab) => tab.getAttribute("aria-selected") === "true") ||
        tabButtons[0];

      tabs.classList.add("is-ready");
      tabs.dataset.ddReady = "true";
      activateTab(initiallySelected);
    });
  };

  const parseVehicleData = (selector) => {
    const dataElement = selector.querySelector(SELECTORS.vehicleData);

    if (!dataElement) {
      return null;
    }

    try {
      const parsedData = JSON.parse(dataElement.textContent.trim());
      return parsedData && typeof parsedData === "object" ? parsedData : null;
    } catch (error) {
      console.error("DRLs Direct: Invalid vehicle selector data.", error);
      return null;
    }
  };

  const normalizeModels = (models) => {
    if (!Array.isArray(models)) {
      return [];
    }

    return models
      .map((model) => {
        if (typeof model === "string") {
          return { label: model, url: "" };
        }

        if (!model || typeof model !== "object") {
          return null;
        }

        return {
          label: String(model.label || model.name || "").trim(),
          url: getSafeProductUrl(model.url)
        };
      })
      .filter((model) => model && model.label);
  };

  const setActionState = (action, isEnabled, url = "") => {
    if (!action) {
      return;
    }

    const isLink = action.tagName === "A";

    if (isLink) {
      action.setAttribute("aria-disabled", String(!isEnabled));
      action.tabIndex = isEnabled ? 0 : -1;

      if (isEnabled && url) {
        action.href = url;
      } else {
        action.removeAttribute("href");
      }
    } else {
      action.disabled = !isEnabled;
      action.dataset.ddVehicleUrl = isEnabled ? url : "";
    }
  };

  const initializeVehicleSelectors = (root) => {
    root.querySelectorAll(SELECTORS.vehicleSelector).forEach((selector) => {
      if (selector.dataset.ddReady === "true") {
        return;
      }

      const brandSelect = selector.querySelector(SELECTORS.brandSelect);
      const modelSelect = selector.querySelector(SELECTORS.modelSelect);
      const action = selector.querySelector(SELECTORS.vehicleAction);
      const status = selector.querySelector(SELECTORS.vehicleStatus);
      const vehicleData = parseVehicleData(selector);

      if (!brandSelect || !modelSelect || !action || !vehicleData) {
        return;
      }

      if (status) {
        status.setAttribute("aria-live", "polite");
      }

      const labels = {
        brandPlaceholder:
          selector.dataset.ddBrandPlaceholder || "Choose a brand",
        modelPlaceholder:
          selector.dataset.ddModelPlaceholder || "Choose a model",
        modelDisabled:
          selector.dataset.ddModelDisabled || "Select a brand first",
        noProduct:
          selector.dataset.ddNoProduct || "No product link is currently available for this model."
      };

      const replaceOptions = (select, placeholder, options = []) => {
        select.replaceChildren();

        const defaultOption = document.createElement("option");
        defaultOption.value = "";
        defaultOption.textContent = placeholder;
        select.appendChild(defaultOption);

        options.forEach((optionData) => {
          const option = document.createElement("option");
          option.value = optionData.label;
          option.textContent = optionData.label;
          option.dataset.url = optionData.url;
          select.appendChild(option);
        });
      };

      const brands = Object.keys(vehicleData)
        .filter((brand) => normalizeModels(vehicleData[brand]).length > 0)
        .sort((first, second) => first.localeCompare(second));

      replaceOptions(
        brandSelect,
        labels.brandPlaceholder,
        brands.map((brand) => ({ label: brand, url: "" }))
      );
      replaceOptions(modelSelect, labels.modelDisabled);
      modelSelect.disabled = true;
      setActionState(action, false);

      brandSelect.addEventListener("change", () => {
        const selectedBrand = brandSelect.value;
        const models = normalizeModels(vehicleData[selectedBrand]);

        replaceOptions(
          modelSelect,
          selectedBrand ? labels.modelPlaceholder : labels.modelDisabled,
          models
        );

        modelSelect.disabled = !selectedBrand || models.length === 0;
        setActionState(action, false);

        if (status) {
          status.textContent = "";
        }
      });

      modelSelect.addEventListener("change", () => {
        const selectedOption = modelSelect.options[modelSelect.selectedIndex];
        const productUrl = selectedOption?.dataset.url || "";
        const hasProduct = Boolean(modelSelect.value && productUrl);

        setActionState(action, hasProduct, productUrl);

        if (status) {
          status.textContent =
            modelSelect.value && !productUrl ? labels.noProduct : "";
        }
      });

      action.addEventListener("click", (event) => {
        const isDisabled = action.getAttribute("aria-disabled") === "true";
        const productUrl =
          action.tagName === "A" ? action.getAttribute("href") : action.dataset.ddVehicleUrl;

        if (isDisabled || !productUrl) {
          event.preventDefault();
          return;
        }

        if (action.tagName !== "A") {
          event.preventDefault();
          window.location.assign(productUrl);
        }
      });

      selector.dataset.ddReady = "true";
    });
  };

  const initialize = (scope = document) => {
    getArticleRoots(scope).forEach((root) => {
      initializeTabs(root);
      initializeVehicleSelectors(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();